import {
	Checkbox,
	Form,
	FormItem,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { SettingsPage } from "../SettingsPage";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	ConnectionModel,
	ConnectionViewModel,
	createConnection,
	getCreateConnectionViewModel,
	getEditConnectionViewModel,
	getViewConnectionViewModel,
	updateConnection
} from "client/Api/ClientlessRdp/clientlessRdp";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { isNotNil } from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { clientlessRdpRouting } from "./GatewayOverview";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import styles from "./ConnectionConfiguration.module.less";

const validationConfiguration: IValidationConfiguration<ConnectionModel> = createValidationConfiguration<ConnectionModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("serverAddress")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type ConnectionConfigurationProps = {
	isReadOnly: boolean;
	gatewayId: number;
	connectionId?: string;
	onNavigate: NavigationCallback;
};

const ConnectionConfiguration = (props: ConnectionConfigurationProps): ReactElement => {
	const {
		isReadOnly,
		gatewayId,
		connectionId,
		onNavigate
	} = props;

	const [model, setModel] = useState<ConnectionModel>(createEmptyConnectionModel(gatewayId));
	const [isMakingRequest, setIsMakingRequest] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.ClientlessRDP);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsMakingRequest(true);

					let viewModel: ConnectionViewModel;

					if (isNotNil(connectionId)) {
						if (connectionId === "") {
							throw new Error("Connection id cannot be an empty string");
						}
						if (isReadOnly) {
							viewModel = await getViewConnectionViewModel(
								gatewayId,
								connectionId,
								signal
							)
								.catch(handlePageDataLoadingError);
						} else {
							viewModel = await getEditConnectionViewModel(
								gatewayId,
								connectionId,
								signal
							)
								.catch(handlePageDataLoadingError);
						}
					} else {
						if (isReadOnly) {
							throw new Error("ReadOnly mode is not supported for the new connections");
						} else {
							viewModel = await getCreateConnectionViewModel(gatewayId, signal)
								.catch(handlePageDataLoadingError);
						}
					}

					if (isNotNil(viewModel.model)) {
						setModel(viewModel.model);
					} else {
						setModel(createEmptyConnectionModel(gatewayId));
					}

					setIsMakingRequest(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsMakingRequest(false);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			connectionId,
			gatewayId,
			isReadOnly
		]
	);

	const formHandler: IFormHandler<ConnectionModel> = useFormHandler<ConnectionModel>(
		model,
		setModel,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isMakingRequest) {
				return;
			}
			setIsMakingRequest(true);

			const validationResult: InputValidationResult = await formHandler.validation.validate();
			if (validationResult.kind !== InputValidationResultKind.Valid) {
				alertManager.showError(localize("_NotAllRequiredFieldsAreFilledIn"));
				setIsMakingRequest(false);
				return;
			}

			try {
				if (!connectionId) {
					await createConnection(gatewayId, model);
				} else {
					await updateConnection(
						gatewayId,
						connectionId,
						model
					);
				}

				onNavigate(clientlessRdpRouting.gatewayDetails({gatewayId: `${gatewayId}`}));
			} catch (e) {
				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_UnknownError"));
				}

				setIsMakingRequest(false);
			}
		},
		[
			isMakingRequest,
			formHandler.validation,
			alertManager,
			connectionId,
			gatewayId,
			model,
			onNavigate
		]
	);

	const defaultActions: readonly ConnectionAction[] = useMemo(
		() => listDefaultActions({
			saveHandler,
			backHandler: (gatewayId) => onNavigate(clientlessRdpRouting.gatewayDetails({gatewayId: `${gatewayId}`})),
			editHandler: (gatewayId, connectionId) => onNavigate(
				clientlessRdpRouting.editConnection({
					gatewayId: `${gatewayId}`,
					connectionId
				})
			)
		}),
		[onNavigate, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => {
			return {
				gatewayId,
				connectionId,
				isEditing: !isReadOnly,
			};
		},
		[
			connectionId,
			gatewayId,
			isReadOnly
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isMakingRequest}
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_ConfigureConnection")}
			onBack={() => onNavigate(clientlessRdpRouting.gatewayDetails({gatewayId: `${gatewayId}`}))}
		>
			<Form
				onSubmit="NONE"
				hasWidthConstrained
			>
				<FormItem label={localize("_Name")} isRequired>
					<TextInput
						{...formHandler.for("name").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("name").attachToValidationError()} />
				</FormItem>

				<FormItem label={localize("_ServerAddress")} isRequired>
					<TextInput
						{...formHandler.for("serverAddress").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("serverAddress").attachToValidationError()} />
				</FormItem>

				<FormItem label={localize("_Port")}>
					<TextInput
						{...formHandler.for("port").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("port").attachToValidationError()} />
				</FormItem>

				<FormItem label={localize("_DisplayDesktopBackground")}>
					<Checkbox
						{...formHandler.for("enableBackground").attachToCheckbox()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("enableBackground").attachToValidationError()} />
				</FormItem>
				<h3 className={styles.subTitle}>{localize("_RemoteApp")}</h3>

				<FormItem label={localize("_Program")}>
					<TextInput
						{...formHandler.for("program").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("program").attachToValidationError()} />
				</FormItem>

				<FormItem label={localize("_WorkDirectory")}>
					<TextInput
						{...formHandler.for("workDirectory").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("workDirectory").attachToValidationError()} />
				</FormItem>

				<FormItem label={localize("_Parameters")}>
					<TextInput
						{...formHandler.for("parameters").attachToTextInput()}
						isDisabled={isReadOnly}
					/>
					<ValidationError {...formHandler.for("parameters").attachToValidationError()} />
				</FormItem>

				<h3 className={styles.subTitle}>{localize("_TerminalServerGateway")}</h3>

				<FormItem label={localize("_Hostname")}>
					<TextInput
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("hostName")
							.attachToTextInput()
						}
						isDisabled={isReadOnly}
					/>
					<ValidationError
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("hostName")
							.attachToValidationError()
						}
					/>
				</FormItem>

				<FormItem label={localize("_Port")}>
					<TextInput
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("port")
							.attachToTextInput()
						}
						isDisabled={isReadOnly}
					/>
					<ValidationError
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("port")
							.attachToValidationError()
						}
					/>
				</FormItem>

				<FormItem label={localize("_Domain")}>
					<TextInput
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("domain")
							.attachToTextInput()
						}
						isDisabled={isReadOnly}
					/>
					<ValidationError
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("domain")
							.attachToValidationError()
						}
					/>
				</FormItem>

				<FormItem label={localize("_LoadbalanceInfo")}>
					<TextInput
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("loadBalanceInfo")
							.attachToTextInput()
						}
						isDisabled={isReadOnly}
					/>
					<ValidationError
						{...formHandler
							.for("terminalServerGatewayParameters")
							.for("loadBalanceInfo")
							.attachToValidationError()
						}
					/>
				</FormItem>
			</Form>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
ConnectionConfiguration.displayName = "ConnectionConfiguration";

export type CreateConnectionConfigurationParams = RouteParametersFromProps<
	ConnectionConfigurationProps,
	"gatewayId"
>;

export type EditConnectionConfigurationParams = RouteParametersFromProps<
	ConnectionConfigurationProps,
	"gatewayId" | "connectionId"
>;

export const ConnectionConfigurationForRouter: FunctionComponent<Pick<ConnectionConfigurationProps, "isReadOnly">> = connectToRouter(
	ConnectionConfiguration,
	{
		"gatewayId": {
			isRequired: true,
			tryParse: tryParseInt
		},
		"connectionId": {
			isRequired: false
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type ConnectionActionContext = {
	gatewayId: number;
	connectionId: string | nil;
	isEditing: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

type DefaultActionsHandlers = {
	saveHandler(): void;
	backHandler(gatewayId: number): void;
	editHandler(gatewayId: number, connectionId: string): void;
};

function listDefaultActions(
	options: DefaultActionsHandlers
): readonly ConnectionAction[] {
	const {
		saveHandler,
		backHandler,
		editHandler
	} = options;

	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: (c) => c.isEditing,
			execute: () => saveHandler(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (c) => c.isEditing,
			execute: (c) => backHandler(c.gatewayId)
		},
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (c) => Boolean(c.connectionId) && !c.isEditing,
			execute: (c) => editHandler(c.gatewayId, c.connectionId!)
		},
	];
}

function createEmptyConnectionModel(gatewayId: number): ConnectionModel {
	return {
		gatewayId,
		id: null,
		name: "",
		serverAddress: "",
		port: "",
		enableBackground: false,
		program: "",
		workDirectory: "",
		parameters: "",
		terminalServerGatewayParameters: {
			hostName: "",
			port: "",
			domain: "",
			loadBalanceInfo: ""
		}
	};
}
